import { Box, Group, Stack, Text, Title } from "@mantine/core"
import { PlusIcon } from "@phosphor-icons/react"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"
import { IDeliveryNotePlus } from "../../models/DeliveryNote"
import { DeliveryActionButton } from "../delivery/action-controls"
import { useDeliveryActions } from "../delivery/delivery-actions"
import { PendingDeliveriesTable } from "./PendingDeliveriesTable"
import { PendingRowMeta } from "./hooks"

interface Props {
  district: string
  rows: IDeliveryNotePlus[]
  selectedIds: string[]
  onToggleSelect: (id: string) => void
  onToggleSelectAll: () => void
  onAssignOne: (dn: IDeliveryNotePlus) => void
  onProgressNote?: (dn: IDeliveryNotePlus) => void
  onViewDetail?: (dn: IDeliveryNotePlus) => void
  onCreateRouteForGroup: (rows: IDeliveryNotePlus[]) => void
  creatingRoute: boolean
  metaByDn: Map<string, PendingRowMeta>
  countsByDistrict: Map<string, number>
}

/** Same receiver + same delivery day: DNs that could be consolidated (in-page hint only). */
function consolidatableCount(rows: IDeliveryNotePlus[]): number {
  const buckets = new Map<string, number>()
  for (const dn of rows) {
    const raw = (dn as { scheduledDate?: string | null }).scheduledDate ?? dn.createdAt ?? null
    const key = `${dn.receiverBusiness?.refCode ?? "?"}|${raw ? dayjs(raw).format("YYYY-MM-DD") : "?"}`
    buckets.set(key, (buckets.get(key) ?? 0) + 1)
  }
  return Array.from(buckets.values())
    .filter((n) => n > 1)
    .reduce((s, n) => s + n, 0)
}

/** «Бүсээр бүлэглэх» group: district heading + its rows. */
export function PendingDeliveryGroupCard({ district, rows, onCreateRouteForGroup, creatingRoute, ...tableProps }: Props) {
  const { t } = useTranslation("delivery")
  const { permissionState } = useDeliveryActions()
  const totalSize = rows.reduce((sum, dn) => sum + (parseFloat(dn.classification?.totalSizeValue ?? "0") || 0), 0)
  const consolidatable = consolidatableCount(rows)

  return (
    <Stack gap="sm">
      <Group justify="space-between" align="flex-end" wrap="wrap">
        <Box>
          <Title order={4} fz="var(--nw-fz-card)">
            {district}
          </Title>
          <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
            {t("manage.pending.groupMeta", { count: rows.length, size: totalSize.toFixed(0) })}
            {consolidatable > 0 && ` · ${t("manage.pending.groupConsolidatable", { count: consolidatable })}`}
          </Text>
        </Box>
        <DeliveryActionButton
          state={permissionState("createRoute")}
          variant="default"
          leftSection={<PlusIcon size={16} />}
          loading={creatingRoute}
          onClick={() => onCreateRouteForGroup(rows)}
        >
          {t("manage.action.createRoute")}
        </DeliveryActionButton>
      </Group>
      <PendingDeliveriesTable rows={rows} {...tableProps} />
    </Stack>
  )
}
